import React from 'react';
import { Firestore } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { History, Plus, Loader, AlertCircle, CheckCircle, XCircle, Clock, Database, ArrowLeft, Image, Video, Eraser } from 'lucide-react';
import { useBulkTreatments } from '../../hooks/useBulkTreatments';

interface TreatmentsHistoryProps {
  db: Firestore;
  uid: string;
}

const TreatmentsHistory: React.FC<TreatmentsHistoryProps> = ({ db, uid }) => {
  const navigate = useNavigate();
  const { project_id } = useParams<{ project_id: string }>();
  const { treatments, loading, error } = useBulkTreatments(db, project_id, uid);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'processing':
        return <Loader className="w-5 h-5 text-blue-400 animate-spin" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-gray-400" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'processing':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      case 'failed':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      case 'cancelled':
        return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
      default:
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    }
  };

  const getMediaTypeIcon = (mediaType: 'image' | 'video') => {
    switch (mediaType) {
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'image':
      default:
        return <Image className="w-4 h-4" />;
    }
  };

  const getMediaTypeColor = (mediaType: 'image' | 'video') => {
    switch (mediaType) {
      case 'video':
        return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
      case 'image':
      default:
        return 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const calculateProgress = (processed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((processed / total) * 100);
  };

  if (loading) {
    return (
      <div className="text-gray-100 flex flex-col">
        <div className="flex-grow flex items-center justify-center">
          <Loader className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="text-gray-100 flex flex-col">
      <main className="flex-grow w-full max-w-7xl mx-auto p-4 md:p-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Start Screen
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl border border-blue-500/30">
              <History className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Bulk Treatments
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                View and monitor your AI processing jobs
              </p>
            </div>
          </div>
          
          <button
            onClick={() => navigate(`/projects/${project_id}/bulk-treatments/new`)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Treatment
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-300 font-medium">Error loading treatments</p>
              <p className="text-xs text-red-400 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Treatments List */}
        {treatments.length === 0 ? (
          <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-12 text-center">
            <Database className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No treatments yet</h3>
            <p className="text-gray-400 mb-6">
              Create your first bulk treatment to process data with AI
            </p>
            <button
              onClick={() => navigate(`/projects/${project_id}/bulk-treatments/new`)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-2.5 px-5 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Treatment
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {treatments.map((treatment) => {
              const progress = calculateProgress(treatment.processed_rows, treatment.total_rows);
              
              return (
                <div
                  key={treatment.treatment_id}
                  className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 hover:border-gray-600/50 transition-all cursor-pointer"
                  onClick={() => navigate(`/projects/${project_id}/bulk-treatments/${treatment.treatment_id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{treatment.name}</h3>
                        <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getMediaTypeColor(treatment.media_type)}`}>
                          {getMediaTypeIcon(treatment.media_type)}
                          {treatment.media_type.toUpperCase()}
                        </span>
                        {treatment.media_type === 'image' && treatment.remove_background && (
                          <span className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border text-emerald-400 bg-emerald-500/20 border-emerald-500/30">
                            <Eraser className="w-3 h-3" />
                            BG REMOVAL
                          </span>
                        )}
                        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(treatment.status)}`}>
                          {getStatusIcon(treatment.status)}
                          {treatment.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 font-mono">
                        {treatment.bigquery_config.project_id}.{treatment.bigquery_config.dataset_id}.{treatment.bigquery_config.table_name}
                      </p>
                    </div>
                    
                    <div className="text-right text-sm text-gray-400">
                      <div>{formatDate(treatment.created_at)}</div>
                      {treatment.status === 'processing' && (
                        <div className="text-blue-400 font-medium mt-1">
                          {progress}% complete
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {treatment.status === 'processing' && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                        <span>Processing rows...</span>
                        <span>{treatment.processed_rows.toLocaleString()} / {treatment.total_rows.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-gray-700/50 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-500 rounded-full"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Stats Row */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-400">{treatment.total_rows.toLocaleString()} rows</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400">{treatment.bigquery_config.selected_columns.length} columns</span>
                    </div>
                    {treatment.results_table && (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">•</span>
                          <span className="text-green-400">Results available</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Error Message */}
                  {treatment.status === 'failed' && treatment.error_message && (
                    <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <p className="text-sm text-red-300">{treatment.error_message}</p>
                    </div>
                  )}

                  {/* Prompt Preview */}
                  <div className="mt-4 bg-gray-900/50 rounded-lg p-3">
                    {
                      treatment.prompt_rules ? (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Prompt Rules:</div>
                          <div className="text-sm text-gray-300 font-mono line-clamp-2">
                            {JSON.stringify(treatment.prompt_rules, null, 2)}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Pattern Rules:</div>
                          <div className="text-sm text-gray-300 font-mono line-clamp-2">
                            {JSON.stringify(treatment.pattern_rules, null, 2)}
                          </div>
                        </div>
                      )
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default TreatmentsHistory;
